/**
 * RoomChatRail — compact horizontal rail shown above the f8 chat prompts.
 *
 * Renders a scrollable row of chips (one per active/pending mediation).
 * - Pending invitations (state === 'invited' && !isInviter) lead with a magenta
 *   accent chip to draw the eye.
 * - Other active states use the per-state colour palette from RoomList.
 * - Ended mediations are omitted (the list page surfaces them).
 * - Returns null when the user has no active mediations or pending invitations
 *   so users with no mediations see no rail at all.
 *
 * Data model: reuses `getMyRooms()` + `routeFor()` verbatim from
 * RoomList so there is one canonical routing table.
 *
 * PPL-45.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GitMerge } from 'react-feather';
import { useAuth } from '../hooks/useAuth';
import { getMyRooms } from '../services/roomService';
import { capture } from '../services/posthog';
import type { RoomListItem, RoomState } from '../types/room';
import MonoAvatar from './MonoAvatar';
import '../styles/RoomChatRail.css';

// ─── helpers (duplicated from RoomList intentionally — no shared module
//     created; this PR doesn't refactor the list page) ────────────────────────

/** One-line "what's next?" label for a chip. */
function chipBlurb(item: RoomListItem): string {
  const them = item.otherParticipant.firstName || 'them';
  switch (item.state) {
    case 'invited':
      return item.isInviter ? `waiting for ${them}` : `${them} invited you`;
    case 'prep':        return 'in prep';
    case 'rules_set':   return 'set rules';
    case 'joint':       return 'opening ceremony';
    case 'ongoing':     return 'room open';
    case 'paused':      return 'paused';
    case 'ended':       return 'ended';
  }
}

/** CSS class driving the accent colour of each chip. */
function chipTone(item: RoomListItem): string {
  if (item.state === 'invited' && !item.isInviter) return 'mcrail-chip--invite';
  const toneMap: Record<RoomState, string> = {
    invited:   'mcrail-chip--violet',
    prep:      'mcrail-chip--violet',
    rules_set: 'mcrail-chip--amber',
    joint:     'mcrail-chip--pink',
    ongoing:   'mcrail-chip--teal',
    paused:    'mcrail-chip--muted',
    ended:     'mcrail-chip--muted',
  };
  return toneMap[item.state] ?? 'mcrail-chip--muted';
}

/** Route a chip tap to the right screen — mirrors RoomList.routeFor exactly. */
function routeFor(item: RoomListItem): string {
  const id = item.id;
  switch (item.state) {
    case 'invited':
      return item.isInviter ? `/rooms/${id}` : `/rooms/${id}/invite`;
    case 'prep':      return `/rooms/${id}/prep`;
    case 'rules_set': return `/rooms/${id}/consent`;
    case 'joint':     return `/rooms/${id}/opener`;
    case 'ongoing':   return `/rooms/${id}/room`;
    case 'paused':    return `/rooms/${id}/room`;
    case 'ended':     return `/rooms/${id}/summary`;
  }
}

// ─── chip ────────────────────────────────────────────────────────────────────

interface ChipProps {
  item: RoomListItem;
  onTap: () => void;
}

function RoomChip({ item, onTap }: ChipProps) {
  const name = item.otherParticipant.firstName || 'them';
  const tone = chipTone(item);
  const blurb = chipBlurb(item);
  const isInvite = item.state === 'invited' && !item.isInviter;

  return (
    <button
      type="button"
      className={`mcrail-chip ${tone}`}
      onClick={onTap}
      aria-label={`Room with ${name}: ${blurb}`}
    >
      <MonoAvatar name={name} size={24} />
      <span className="mcrail-chip-body">
        <span className="mcrail-chip-name">{name}</span>
        <span className="mcrail-chip-blurb">{blurb}</span>
      </span>
      {isInvite && <span className="mcrail-chip-dot" aria-hidden="true" />}
    </button>
  );
}

// ─── rail ────────────────────────────────────────────────────────────────────

export default function RoomChatRail() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState<RoomListItem[]>([]);

  useEffect(() => {
    if (!user?.uid) return;
    let cancelled = false;
    getMyRooms()
      .then((res) => {
        if (cancelled) return;
        // Invitations first, then active (non-ended) mediations. Ended omitted.
        const invitations = (res.pendingInvitations || []);
        const active = (res.mediations || []).filter(
          (m) => m.state !== 'ended'
        );
        // De-duplicate: pendingInvitations is a subset of mediations already,
        // but the server may return them independently; use id as the key.
        const seen = new Set<string>();
        const merged: RoomListItem[] = [];
        for (const item of [...invitations, ...active]) {
          if (!seen.has(item.id)) {
            seen.add(item.id);
            merged.push(item);
          }
        }
        setItems(merged);
      })
      .catch(() => {
        // Silently swallow — the rail is optional chrome; errors must not
        // block the main chat UI.
      });
    return () => { cancelled = true; };
  }, [user?.uid]);

  // Empty-state guarantee: nothing rendered for users with no active mediations.
  if (items.length === 0) return null;

  return (
    <div className="mcrail-root" aria-label="Your rooms">
      <div className="mcrail-label">
        <GitMerge size={11} className="mcrail-label-icon" aria-hidden="true" />
        <span>rooms</span>
      </div>
      <div className="mcrail-scroll">
        {items.map((item) => (
          <RoomChip
            key={item.id}
            item={item}
            onTap={() => {
              capture('room_cta_tapped', {
                cta: 'room_rail_chip',
                action: 'resume',
                mediation_id: item.id,
                platform: 'web',
              });
              navigate(routeFor(item));
            }}
          />
        ))}
      </div>
    </div>
  );
}
